/**
 * schema.org graphs. These are what let an answer engine state BadgeReg's price and
 * audience as fact rather than inferring them from marketing prose.
 */

import {
  OG_IMAGE,
  PRICING_SENTENCE,
  PRODUCT,
  SITE_URL,
  absoluteUrl,
} from "./siteMeta";

const ORGANIZATION_ID = `${SITE_URL}/#organization`;
const WEBSITE_ID = `${SITE_URL}/#website`;

export const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": ORGANIZATION_ID,
  name: PRODUCT.name,
  legalName: PRODUCT.legalName,
  url: SITE_URL,
  logo: `${SITE_URL}/android-chrome-512x512.png`,
  image: OG_IMAGE,
  description: PRODUCT.shortDescription,
  founder: {
    "@type": "Person",
    name: PRODUCT.author.name,
    url: PRODUCT.author.url,
  },
  contactPoint: {
    "@type": "ContactPoint",
    contactType: "sales",
    email: PRODUCT.supportEmail,
    url: absoluteUrl("/contact"),
    availableLanguage: ["en"],
  },
};

export const webSiteSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": WEBSITE_ID,
  url: SITE_URL,
  name: PRODUCT.name,
  description: PRODUCT.shortDescription,
  publisher: { "@id": ORGANIZATION_ID },
  inLanguage: "en-US",
};

/**
 * The pricing model is two additive components, which `Offer` cannot express in a single
 * price. A PriceSpecification array states both explicitly so nothing has to infer that
 * "$250" and "$2.50" combine.
 */
export const softwareApplicationSchema = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  "@id": `${SITE_URL}/#software`,
  name: PRODUCT.name,
  applicationCategory: PRODUCT.applicationCategory,
  applicationSubCategory: "Event registration software",
  operatingSystem: PRODUCT.operatingSystem,
  url: SITE_URL,
  description: PRODUCT.shortDescription,
  audience: {
    "@type": "Audience",
    audienceType: PRODUCT.audience,
  },
  provider: { "@id": ORGANIZATION_ID },
  offers: [
    {
      "@type": "Offer",
      name: "Event setup fee",
      price: PRODUCT.setupFee,
      priceCurrency: PRODUCT.currency,
      description: `One-time setup fee of $${PRODUCT.setupFee} per merit badge college event.`,
      url: absoluteUrl("/pricing"),
      category: "Setup fee",
    },
    {
      "@type": "Offer",
      name: "Per registered Scout",
      price: PRODUCT.perScoutFee,
      priceCurrency: PRODUCT.currency,
      description: `$${PRODUCT.perScoutFee.toFixed(2)} per Scout registered through the platform.`,
      url: absoluteUrl("/pricing"),
      category: "Usage fee",
    },
  ],
  featureList: [
    "Online Scout registration for merit badge colleges",
    "Class scheduling with capacity limits and age restrictions",
    "Multiple sessions of the same merit badge class",
    "PayPal payment collection and email receipts",
    "Registration open and close windows",
    "Participant and counselor reporting with CSV and Excel export",
    "Transaction ledger and audit log",
    "Per-event branding, custom colors, logo and flyer",
    "Custom FAQs, prerequisites and terms and conditions",
  ],
};

export function faqPageSchema(faqs) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "@id": `${absoluteUrl("/faqs")}#faq`,
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  };
}

export function breadcrumbSchema(name, path) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: absoluteUrl("/"),
      },
      {
        "@type": "ListItem",
        position: 2,
        name,
        item: absoluteUrl(path),
      },
    ],
  };
}

/** Stated as an Offer-bearing product page so the two-part price is machine-readable. */
export const pricingPageSchema = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  "@id": `${absoluteUrl("/pricing")}#webpage`,
  url: absoluteUrl("/pricing"),
  name: "BadgeReg pricing",
  description: PRICING_SENTENCE,
  isPartOf: { "@id": WEBSITE_ID },
  about: { "@id": `${SITE_URL}/#software` },
};

/**
 * Page-level schema for the prose pages.
 *
 * `AboutPage` and `PrivacyPolicy` are real schema.org types and worth using over a bare `WebPage`:
 * they tell a crawler what kind of page it is looking at, which is what lets an assistant answer
 * "who makes BadgeReg" or "what does BadgeReg do with my child's data" by citing the right URL
 * instead of the home page. `mainEntityOfPage` ties each back to the product graph.
 */
function prosePageSchema(type, path, name, description) {
  return {
    "@context": "https://schema.org",
    "@type": type,
    "@id": `${absoluteUrl(path)}#webpage`,
    url: absoluteUrl(path),
    name,
    description,
    isPartOf: { "@id": WEBSITE_ID },
    about: { "@id": `${SITE_URL}/#software` },
    publisher: { "@id": ORGANIZATION_ID },
    inLanguage: "en-US",
  };
}

export const aboutPageSchema = prosePageSchema(
  "AboutPage",
  "/about",
  "About BadgeReg",
  "BadgeReg is built by Scouting volunteers. Who makes it, why it exists, and how the $250 setup fee and $2.50 per-Scout fee fund it.",
);

export const securityPageSchema = prosePageSchema(
  "WebPage",
  "/security",
  "BadgeReg security",
  "How BadgeReg protects Scout and parent data: HTTPS, bcrypt password hashing, httpOnly session cookies with CSRF tokens, role-based access, per-event data separation, and PayPal-hosted card entry.",
);

export const privacyPageSchema = prosePageSchema(
  "PrivacyPolicy",
  "/privacy",
  "BadgeReg privacy policy",
  "What personal data BadgeReg collects about Scouts, parents and organizers, why it is held, who it is shared with, how long it is kept, and how to have it corrected or deleted.",
);

export const accessibilityPageSchema = prosePageSchema(
  "WebPage",
  "/accessibility",
  "BadgeReg accessibility statement",
  "BadgeReg's marketing site targets WCAG 2.2 Level AA, checked automatically on every build. What is covered, the known limitations, and how to report a barrier.",
);

export const termsPageSchema = prosePageSchema(
  "TermsOfService",
  "/terms",
  "BadgeReg terms of service",
  "Terms covering use of BadgeReg: fees, PayPal payment processing, event cancellation and refunds, acceptable use, data ownership, availability, and limits of liability.",
);
